import { useState, useCallback, useEffect } from 'react';
import MainLayout from 'views/layouts/Main';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Container, StepsContainer, ConfigsContainer, ButtonsContainer } from './styled';
import { H2 } from 'views/components/UI/Text';
import Steps from 'views/components/Stepper'
import { Button, BackButton, StrokedButton } from 'views/components/UI/Buttons';
import BrainQuadrant from 'views/containers/BrainQuadrant';
import Tooltip from 'views/components/Tooltip'
import Select from 'views/components/UI/Select';
import { STEPS_NEW_PROCESS } from '../constants';
import { platformAdminRoutes } from 'router/routes';
import { useNotification } from 'lib/context/notification.context';
import { stepOneSelector, stepThreeSelector, stepFourSelector, setStepFourTrigger, getBetesaSkillIds, setStepFour } from 'ducks/createProcess';
import { ALERT_LEVELS, BetesaIdealProfile, BetesaStepFourData } from 'types/common';
import { NEW_PROCESS_TRUE } from 'constants/global.constants';

function transformToNumberArray(input: string): number[] {
  const stringArray = input.replace(/,$/, "").split(",");
  const numberArray = stringArray.map((str) => parseInt(str, 10));
  return numberArray;
}

const REQUIRED_BETESA_QUADRANTS: Array<BetesaIdealProfile['quadrant']> = ['FI', 'FD', 'BI', 'BD'];
const BETESA_PROFILE_VALIDATION_MESSAGE =
  'Debes completar prioridad y rango esperado en los 4 cuadrantes de Betesa. El valor mínimo de cada cuadrante debe ser 10 y el máximo debe ser 20.';

function validateProfileValues(profileValues: BetesaIdealProfile[]): boolean {
  if (profileValues.length !== REQUIRED_BETESA_QUADRANTS.length) {
    return false;
  }

  const profileValuesByQuadrant = new Map(
    profileValues.map((profile) => [profile.quadrant, profile])
  );

  return REQUIRED_BETESA_QUADRANTS.every((quadrant) => {
    const profile = profileValuesByQuadrant.get(quadrant);

    if (!profile) {
      return false;
    }

    return (
      profile.priority >= 1 &&
      profile.priority <= 4 &&
      profile.min >= 10 &&
      profile.max >= 20 &&
      profile.max - profile.min >= 10
    );
  });
}

function IdealProfileBetesa() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getSuccess, getWarning } = useNotification();
  const location = useLocation();

  const priorityQuadrant = location.state?.priorityQuadrant || '' as string;
  const stepOneData = useSelector(stepOneSelector);
  const stepThreeData = useSelector(stepThreeSelector);
  const dataStepFour = useSelector(stepFourSelector);
  const betesaSkills = useSelector(getBetesaSkillIds)

  const alertLevelLabels = Object.keys(ALERT_LEVELS);

  const alertLevelOptions = Object.values(ALERT_LEVELS).map((value: number[], index) => {
    return {
      value: `${value}`,
      label: alertLevelLabels[index],
    };
  });
  const [alertLevel, setAlertLevel] = useState<number[] | string>([])
  const [betesaIdealProfile, setBetesaIdealProfile] = useState<BetesaIdealProfile[]>(dataStepFour?.betesaIdealProfile?.profileValues || [])

  const useSkillsTest = stepThreeData.useSkillsTest
  const hasAlertLevelSelected = Boolean(alertLevel?.length)
  const isBetesaConfigurationComplete = validateProfileValues(betesaIdealProfile)

  const handleNextStep = useCallback(() => {
    if (!hasAlertLevelSelected) {
      getWarning('Debes seleccionar un nivel de alerta')
      return
    }

    if (!isBetesaConfigurationComplete) {
      getWarning(BETESA_PROFILE_VALIDATION_MESSAGE)
      return
    }

    const alertLevelArray = typeof alertLevel === 'string' ? transformToNumberArray(alertLevel as string) : alertLevel

    const dataStepFourToSave = {
      processId: stepOneData.processId,
      betesaIdealProfile: {
        betesaSkills,
        alertLevelId: alertLevelArray || [],
        profileValues: betesaIdealProfile
      },
      isNewProcess: NEW_PROCESS_TRUE
    }

    try {
      if (useSkillsTest) {
        dispatch(setStepFour(dataStepFourToSave));
        dispatch(setStepFourTrigger.run(dataStepFourToSave));
        getSuccess('Perfil ideal de betesa guardado correctamente')
      } else {
        dispatch(setStepFourTrigger.run(dataStepFourToSave));
      }
    } catch (error) {
      console.error('error saving the skills and ideal profile', error)
    } finally {
      if (useSkillsTest) {
        navigate(platformAdminRoutes.ProcessSkills)
      } else {
        navigate(platformAdminRoutes.CreateUsers)
      }
    }
  }, [
    dispatch,
    navigate,
    useSkillsTest,
    betesaSkills,
    alertLevel,
    betesaIdealProfile,
    stepOneData.processId,
    getSuccess,
    getWarning,
    hasAlertLevelSelected,
    isBetesaConfigurationComplete
  ]);

  const handleSkipStep = useCallback(() => {

    const dataStepFourToSave = {
      processId: stepOneData.processId,
      betesaIdealProfile: {
        betesaSkills: [],
        alertLevelId: [],
        profileValues: []
      },
    }

    if (useSkillsTest) {
      dispatch(setStepFour(dataStepFourToSave));
      dispatch(setStepFourTrigger.run(dataStepFourToSave));
      navigate(platformAdminRoutes.ProcessSkills)
    } else {
      dispatch(setStepFourTrigger.run(dataStepFourToSave));
      setTimeout(() => {
        navigate(platformAdminRoutes.CreateUsers)
      }, 1000);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, useSkillsTest, stepOneData.processId, dispatch]);


  useEffect(() => {
    const betesaIdealProfileStore: BetesaStepFourData = dataStepFour?.betesaIdealProfile as BetesaStepFourData;

    if (betesaIdealProfileStore) {
      const alertLevelStore = betesaIdealProfileStore?.alertLevelId
      const profileValues = betesaIdealProfileStore?.profileValues
      setAlertLevel(alertLevelStore)
      setBetesaIdealProfile(profileValues)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainLayout>
      <Container>
        <H2>Determina el Perfil ideal para Betesa</H2>
        <StepsContainer>
          <Steps steps={STEPS_NEW_PROCESS} activeStep={3} />
        </StepsContainer>
        <ConfigsContainer>
          <div style={{ width: '30%', marginLeft: '-10px' }}>
            <Select
              label='Nivel de alerta'
              options={alertLevelOptions as any}
              value={alertLevel}
              onChange={(e) => {
                setAlertLevel(e as any);
              }}
              hideNeverChoice
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
            <Tooltip
              tooltipText='Si decides saltar este paso no podrás utilizar 9Box para este proceso'
              marginTop='8px'
              style={{
                marginRight: '-12px',
                marginBottom: '0px',
                transform: 'scale(1.2)',
              }}
            />
            <StrokedButton type='button' onClick={handleSkipStep}>Saltar este paso</StrokedButton>
          </div>
        </ConfigsContainer>
        <BrainQuadrant
          defaultQuadrant={priorityQuadrant || ''}
          onChange={e => setBetesaIdealProfile(e)}
          quadrantData={betesaIdealProfile}
        />
        <ButtonsContainer>
          <Button
            disabled={!hasAlertLevelSelected || !isBetesaConfigurationComplete}
            type='submit'
            onClick={handleNextStep}
            onDisabledClick={() => {
              if (!hasAlertLevelSelected) {
                getWarning('Debes seleccionar un nivel de alerta')
              }

              if (!isBetesaConfigurationComplete) {
                getWarning(BETESA_PROFILE_VALIDATION_MESSAGE)
              }
            }}
          >
            {useSkillsTest ? 'Configurar siguiente prueba' : 'Agregar usuarios'}
          </Button>
          <BackButton onClick={() => navigate(platformAdminRoutes.SelectTestsType)} />
        </ButtonsContainer>
      </Container>
    </MainLayout>
  );
}

export default IdealProfileBetesa;
